import { createContext, useCallback, useContext, useMemo, useReducer, type ReactNode } from 'react';

// Tipos
export type DrawerType =
  | 'receiving-methods'
  | 'payment-methods'
  | 'coupon'
  | 'change-amount'
  | 'schedule-picker'
  | 'confirm-schedule'
  | 'confirm-cvv'
  | null;

type DrawerState = {
  current: DrawerType;
  previous: DrawerType;
};

type DrawerAction =
  | { type: 'OPEN'; payload: Exclude<DrawerType, null> }
  | { type: 'CLOSE' }
  | { type: 'NEXT'; payload: Exclude<DrawerType, null> };

type DrawerContextType = {
  current: DrawerType;
  previous: DrawerType;
  open: (drawer: Exclude<DrawerType, null>) => void;
  close: () => void;
  next: (drawer: Exclude<DrawerType, null>) => void;
};

function drawerReducer(state: DrawerState, action: DrawerAction): DrawerState {
  switch (action.type) {
    case 'OPEN':
      return {
        previous: null,
        current: action.payload,
      };
    case 'CLOSE':
      return {
        previous: state.current,
        current: null,
      };
    case 'NEXT':
      return {
        previous: state.current,
        current: action.payload,
      };
    default:
      return state;
  }
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export function DrawerProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(drawerReducer, {
    current: null,
    previous: null,
  });

  const open = useCallback((drawer: Exclude<DrawerType, null>) => {
    dispatch({ type: 'OPEN', payload: drawer });
  }, []);

  const close = useCallback(() => {
    dispatch({ type: 'CLOSE' });
  }, []);

  const next = useCallback((drawer: Exclude<DrawerType, null>) => {
    dispatch({ type: 'NEXT', payload: drawer });
  }, []);

  const value = useMemo(
    () => ({
      current: state.current,
      previous: state.previous,
      open,
      close,
      next,
    }),
    [open, close, next, state]
  );

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
}

// eslint-disable-next-line
export function useDrawer() {
  const ctx = useContext(DrawerContext);
  if (!ctx) throw new Error('useDrawer must be used inside DrawerProvider');
  return ctx;
}
